import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, QrCode, Copy, Trash2, Plus, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail, timeAgo } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function Spark({ days }) {
  const peak = Math.max(1, ...days.map((d) => d.scans));
  return (
    <div className="flex items-end gap-[2px] h-8" title="Scans per day · last 14 days">
      {days.map((d) => (
        <span key={d.day} title={`${d.day}: ${d.scans}`}
              className={`w-[5px] rounded-sm ${d.scans ? "bg-[#0A8CF2]" : "bg-slate-800"}`}
              style={{ height: `${Math.max(3, Math.round((d.scans / peak) * 32))}px` }} />
      ))}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className={`font-heading font-black text-lg leading-none ${accent ? "text-[#7FC3FF]" : ""}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mt-0.5">{label}</div>
    </div>
  );
}

export function PrintedCodesPanel({ onCopy }) {
  const [codes, setCodes] = useState(null);
  const [staffOnly, setStaffOnly] = useState(false);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ code: "", label: "", target: "/" });
  const [targets, setTargets] = useState({});

  const load = () => api.get("/platform/print-codes")
    .then(({ data }) => { setCodes(data); setStaffOnly(false); })
    .catch((e) => { setCodes([]); setStaffOnly(e.response?.status === 401 || e.response?.status === 403); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) { toast.error("Give the code a short name, e.g. mailer"); return; }
    setBusy("new");
    try {
      const { data } = await api.post("/platform/print-codes", form);
      setCodes((prev) => [...(prev || []), data]);
      setForm({ code: "", label: "", target: "/" });
      toast.success(`${data.url} is live`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const saveTarget = async (c) => {
    const target = targets[c.code];
    if (!target || target === c.target) return;
    setBusy(c.code);
    try {
      const { data } = await api.patch(`/platform/print-codes/${c.code}`, { target });
      setCodes((prev) => prev.map((x) => (x.code === c.code ? data : x)));
      toast.success(`${c.label} now opens ${data.goes_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.url} and its ${c.scans} recorded scan(s)?`)) return;
    setBusy(c.code);
    try {
      await api.delete(`/platform/print-codes/${c.code}`);
      setCodes((prev) => prev.filter((x) => x.code !== c.code));
      toast.success("Code deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <section className="mt-6 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="printed-codes">
      <h2 className="font-heading font-bold flex items-center gap-2"><QrCode className="h-4 w-4 text-[#0A8CF2]" /> Printed codes · counted scans</h2>
      <p className="text-xs text-slate-400 mt-1">
        Print these instead of a plain link. A scan lands on <code className="text-[#7FC3FF]">/s/&lt;code&gt;</code>, gets counted, then
        bounces straight to the page — the person never notices. Change where a code points any time; already-printed codes keep working.
      </p>

      {codes === null ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0A8CF2]" /></div>
      ) : staffOnly ? (
        <p className="text-sm text-slate-400 mt-4" data-testid="printed-codes-staff-only">Sign in as Vinconex staff (the admin console), then come back to this page.</p>
      ) : (
        <>
          <div className="mt-4 space-y-3">
            {codes.map((c) => (
              <div key={c.code} data-testid={`print-code-${c.code}`} className="rounded-lg border border-[#1C2233] bg-[#05070C] p-4 flex flex-col lg:flex-row gap-4">
                <img src={c.qr} alt={`${c.label} QR`} data-testid={`print-code-qr-${c.code}`} className="h-24 w-24 rounded bg-white p-1 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{c.label}</div>
                  <button type="button" onClick={() => onCopy?.(c.url, "Link")} data-testid={`print-code-copy-${c.code}`}
                          className="mt-0.5 text-xs text-[#7FC3FF] hover:text-white inline-flex items-center gap-1.5">
                    <Copy className="h-3 w-3" /> {c.url}
                  </button>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Input value={targets[c.code] ?? c.target} onChange={(e) => setTargets({ ...targets, [c.code]: e.target.value })}
                           placeholder="/try" data-testid={`print-code-target-${c.code}`}
                           className="h-8 w-[180px] bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 text-xs" />
                    <Button size="sm" onClick={() => saveTarget(c)} disabled={busy === c.code || (targets[c.code] ?? c.target) === c.target}
                            data-testid={`print-code-save-${c.code}`} className="h-8 bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold">Point it here</Button>
                    <a href={c.goes_to} target="_blank" rel="noreferrer" data-testid={`print-code-open-${c.code}`}
                       className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">opens {c.goes_to} <ExternalLink className="h-3 w-3" /></a>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3 text-xs font-semibold">
                    <a href={c.qr} download={`vinconex-qr-${c.code}.png`} data-testid={`print-code-png-${c.code}`} className="text-[#7FC3FF] hover:text-white">PNG</a>
                    <a href={c.svg_url} data-testid={`print-code-svg-${c.code}`} className="text-[#7FC3FF] hover:text-white">SVG vector</a>
                    <button type="button" onClick={() => remove(c)} disabled={busy === c.code} data-testid={`print-code-delete-${c.code}`}
                            className="text-slate-500 hover:text-red-400 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> delete</button>
                  </div>
                </div>
                <div className="lg:w-[300px] shrink-0">
                  <div className="grid grid-cols-4 gap-3" data-testid={`print-code-stats-${c.code}`}>
                    <Stat label="today" value={c.scans_today} accent={c.scans_today > 0} />
                    <Stat label="7 days" value={c.scans_7d} />
                    <Stat label="30 days" value={c.scans_30d} />
                    <Stat label="all time" value={c.scans} />
                  </div>
                  <div className="mt-3"><Spark days={c.days} /></div>
                  <div className="text-[10px] text-slate-500 mt-2 font-mono-tech uppercase tracking-widest" data-testid={`print-code-last-${c.code}`}>
                    {c.last_scan_at ? `last scan ${timeAgo(c.last_scan_at)} · ${c.phones} on a phone · ${c.repeats} repeat` : "no scans yet"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t border-[#1C2233] pt-4 flex flex-col sm:flex-row gap-2" data-testid="print-code-new">
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="code, e.g. mailer"
                   data-testid="print-code-new-code" className="sm:w-[170px] bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="what it's printed on"
                   data-testid="print-code-new-label" className="sm:flex-1 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
            <Input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="/try"
                   data-testid="print-code-new-target" className="sm:w-[150px] bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
            <Button onClick={create} disabled={busy === "new"} data-testid="print-code-create-btn" className="h-10 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
              {busy === "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Add a code</>}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
